import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Search, TrendingDown, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApp } from "@/context/AppContext";
import { NoData } from "@/components/NoData";
import { errMsg, fmtMoney, fmtPeriod, useApi } from "@/lib/api";

const FILTERS = [["all", "All"], ["entity", "Entities"], ["trust", "Trusts"], ["individual", "Individuals"]];
const SORTS = [["name", "Name"], ["change_desc", "Largest increase"], ["change_asc", "Largest decrease"], ["current", "Net worth"]];

const Change = ({ v, pct, testid }) => {
  if (v == null) return <span className="text-xs text-slate-400" data-testid={testid}>—</span>;
  const up = v > 0.004, down = v < -0.004;
  const cls = up ? "text-emerald-700" : down ? "text-red-700" : "text-slate-500";
  return (
    <span className={`num inline-flex items-center gap-1 ${cls}`} data-testid={testid}>
      {up && <TrendingUp className="h-3.5 w-3.5" />}{down && <TrendingDown className="h-3.5 w-3.5" />}
      {fmtMoney(v)}{pct != null && <span className="text-[11px] opacity-75 ml-1">{pct > 0 ? "+" : ""}{pct.toFixed(2)}%</span>}
    </span>
  );
};

const Detail = ({ row }) =>
  row.categories.filter((c) => Math.abs(c.current) > 0.004 || Math.abs(c.prior || 0) > 0.004).map((c) => (
    <tr key={`${c.side}-${c.category}`} className="bg-slate-50/70 text-xs border-t border-slate-200/60">
      <td className="pl-12 pr-5 py-1.5 text-slate-600">{c.side === "liability" ? `Less: ${c.category}` : c.category}</td>
      <td />
      <td className="num text-right">{c.prior == null ? "—" : fmtMoney(c.prior)}</td>
      <td className="num text-right">{fmtMoney(c.current)}</td>
      <td className="text-right pr-5"><Change v={c.change} /></td>
      <td />
    </tr>
  ));

export default function Comparison() {
  const api = useApi();
  const { period } = useApp();
  const [d, setD] = useState(null);
  const [prior, setPrior] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("name");
  const [open, setOpen] = useState({});

  useEffect(() => {
    setPrior("");
  }, [period]);

  useEffect(() => {
    if (!period) return;
    api.get("/comparison", { params: prior ? { period, prior } : { period } })
      .then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api, period, prior]);

  const rows = useMemo(() => {
    if (!d) return [];
    const r = d.rows.filter((x) => (filter === "all" || x.kind === filter) && `${x.name} ${x.code || ""}`.toLowerCase().includes(q.toLowerCase()));
    const s = { name: (a, b) => a.name.localeCompare(b.name), current: (a, b) => b.current - a.current,
      change_desc: (a, b) => (b.change ?? -Infinity) - (a.change ?? -Infinity), change_asc: (a, b) => (a.change ?? Infinity) - (b.change ?? Infinity) };
    return [...r].sort(s[sort]);
  }, [d, q, filter, sort]);

  if (!period) return <NoData />;
  if (!d) return <p className="small-label">Loading…</p>;
  const earlier = d.periods.filter((p) => p < d.period);
  const tot = (k) => rows.filter((r) => r.kind !== "entity").reduce((s, r) => s + (r[k] || 0), 0);
  return (
    <div className="max-w-[1400px]">
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">Month Comparison</h1>
      <p className="text-slate-500 mt-3">Net worth for each entity and person, prior month end beside current. Click a row for the category detail.</p>
      <div className="flex flex-wrap items-center gap-3 mt-10">
        <span className="small-label">Compare</span>
        <Select value={d.prior || ""} onValueChange={setPrior} disabled={!earlier.length}>
          <SelectTrigger className="w-44 h-9 rounded-sm bg-white font-mono text-sm" data-testid="compare-prior-select"><SelectValue placeholder="No earlier month" /></SelectTrigger>
          <SelectContent>{earlier.map((p) => <SelectItem key={p} value={p}>{fmtPeriod(p)}</SelectItem>)}</SelectContent>
        </Select>
        <span className="small-label">with {fmtPeriod(d.period)}</span>
      </div>
      {!d.prior && (
        <p className="mt-6 text-sm text-amber-800 bg-amber-50 border border-amber-200 px-4 py-3 max-w-3xl" data-testid="compare-no-prior">
          There is no earlier month end yet. Upload the next month end's spreadsheets (or an earlier one) and the changes will appear here.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3 mt-6">
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="pl-9 w-full rounded-sm bg-white" data-testid="compare-search-input" />
        </div>
        <div className="flex border border-slate-200 bg-white overflow-x-auto">
          {FILTERS.map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`compare-filter-${v}`}
              className={`px-4 py-2 text-sm whitespace-nowrap transition-colors duration-200 ${filter === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-48 h-9 rounded-sm bg-white text-sm sm:ml-auto" data-testid="compare-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="panel mt-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="compare-table">
          <thead className="bg-slate-100">
            <tr className="small-label text-left"><th className="px-5 py-3">Name</th><th>Type</th>
              <th className="text-right">{d.prior ? fmtPeriod(d.prior) : "Prior"}</th><th className="text-right">{fmtPeriod(d.period)}</th>
              <th className="text-right pr-5">Change</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-200"
                  onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))} data-testid={`compare-row-${r.id}`}>
                  <td className="px-5 py-2.5 flex items-center gap-2">
                    <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${open[r.id] ? "rotate-90" : ""}`} />
                    {r.name}
                  </td>
                  <td className="text-xs font-mono uppercase text-slate-400">{r.kind}</td>
                  <td className="num text-right">{r.prior == null ? "—" : fmtMoney(r.prior)}</td>
                  <td className="num text-right text-slate-900">{fmtMoney(r.current)}</td>
                  <td className="text-right pr-5"><Change v={r.change} pct={r.change_pct} testid={`compare-change-${r.id}`} /></td>
                  <td className="pr-4 text-right">
                    <Link onClick={(e) => e.stopPropagation()} to={`/reports/${r.kind === "entity" ? "entities" : "networth"}/${r.id}`}
                      className="text-xs text-slate-500 hover:text-amber-700 transition-colors duration-200" data-testid={`compare-open-${r.id}`}>Statement</Link>
                  </td>
                </tr>
                {open[r.id] && <Detail row={r} />}
              </Fragment>
            ))}
          </tbody>
          {filter !== "entity" && (
            <tfoot>
              <tr className="border-t-2 border-slate-900 font-semibold">
                <td className="px-5 py-3" colSpan={2}>Total trusts &amp; individuals shown<div className="text-[11px] font-normal text-slate-500">Excludes entities; the dashboard total also adds entities nobody owns.</div></td>
                <td className="num text-right">{d.prior ? fmtMoney(tot("prior")) : "—"}</td>
                <td className="num text-right" data-testid="compare-total-current">{fmtMoney(tot("current"))}</td>
                <td className="text-right pr-5"><Change v={d.prior ? tot("current") - tot("prior") : null} /></td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
